/**
 * Practice guide overlay — invisible trigger zones; on enter, freeze and
 * reveal the next zone through a blurred/darkened mask.
 */
import {
    pointInGuideZone,
    type PracticeGuideStepDef,
    type PracticeGuideMessageSide,
} from "./practice-levels";

export type PracticeGuideAdvanceResult = {
    /** Index of the zone the player must enter next (trigger). */
    triggerIndex: number;
    /** When true, gameplay is frozen and the reveal overlay is shown. */
    frozen: boolean;
    /** Zone index to cut out of the blur/dark mask; -1 = none. */
    revealIndex: number;
};

/**
 * While not frozen: if cursor is in the current trigger zone, freeze and reveal the next zone.
 * While frozen: if cursor is in the revealed zone, either advance to the following reveal or finish.
 */
export function tickPracticeGuide(
    steps: PracticeGuideStepDef[] | undefined,
    state: PracticeGuideAdvanceResult,
    cursorNx: number,
    cursorNy: number,
): PracticeGuideAdvanceResult {
    if (!steps?.length) {
        return { triggerIndex: 0, frozen: false, revealIndex: -1 };
    }

    if (!state.frozen) {
        const t = state.triggerIndex;
        if (t < 0 || t >= steps.length) {
            return { triggerIndex: steps.length, frozen: false, revealIndex: -1 };
        }
        const zone = steps[t]?.zone;
        if (!zone || !pointInGuideZone(cursorNx, cursorNy, zone)) return state;

        const next = t + 1;
        if (next >= steps.length) {
            // Entered the last zone — guide complete.
            return { triggerIndex: steps.length, frozen: false, revealIndex: -1 };
        }
        // Entered zone N → pause and reveal zone N+1.
        return { triggerIndex: next, frozen: true, revealIndex: next };
    }

    // Frozen: waiting for player to enter the revealed zone.
    const r = state.revealIndex;
    if (r < 0 || r >= steps.length) {
        return { triggerIndex: steps.length, frozen: false, revealIndex: -1 };
    }
    const zone = steps[r]?.zone;
    if (!zone || !pointInGuideZone(cursorNx, cursorNy, zone)) return state;

    const next = r + 1;
    if (next >= steps.length) {
        return { triggerIndex: steps.length, frozen: false, revealIndex: -1 };
    }
    return { triggerIndex: next, frozen: true, revealIndex: next };
}

export function initialPracticeGuideState(): PracticeGuideAdvanceResult {
    return { triggerIndex: 0, frozen: false, revealIndex: -1 };
}

function messageBoxRect(
    side: PracticeGuideMessageSide,
    zx: number,
    zy: number,
    zw: number,
    zh: number,
    cw: number,
    ch: number,
): { x: number; y: number; w: number; h: number } {
    const pad = 10;
    const boxW = Math.min(280, cw * 0.42);
    const boxH = Math.min(110, ch * 0.2);
    let x = zx;
    let y = zy;
    if (side === "top") {
        x = zx + zw / 2 - boxW / 2;
        y = zy - boxH - pad;
    } else if (side === "bottom") {
        x = zx + zw / 2 - boxW / 2;
        y = zy + zh + pad;
    } else if (side === "left") {
        x = zx - boxW - pad;
        y = zy + zh / 2 - boxH / 2;
    } else {
        x = zx + zw + pad;
        y = zy + zh / 2 - boxH / 2;
    }
    x = Math.max(8, Math.min(cw - boxW - 8, x));
    y = Math.max(8, Math.min(ch - boxH - 8, y));
    return { x, y, w: boxW, h: boxH };
}

/**
 * Draw blur + dark veil outside the revealed zone (zone stays sharp/normal).
 * Guide trigger areas are never drawn as visible rectangles on their own.
 */
export function drawPracticeGuideOverlay(
    ctx: CanvasRenderingContext2D,
    canvasW: number,
    canvasH: number,
    steps: PracticeGuideStepDef[] | undefined,
    state: PracticeGuideAdvanceResult,
): void {
    if (!steps?.length || !state.frozen || state.revealIndex < 0) return;
    const step = steps[state.revealIndex];
    if (!step?.zone) return;

    const z = step.zone;
    const zx = z.nx * canvasW;
    const zy = z.ny * canvasH;
    const zw = Math.max(2, z.nw * canvasW);
    const zh = Math.max(2, z.nh * canvasH);

    ctx.save();
    // Clip to everything EXCEPT the reveal zone, then blur+darken that region.
    ctx.beginPath();
    ctx.rect(0, 0, canvasW, canvasH);
    ctx.rect(zx, zy, zw, zh);
    ctx.clip("evenodd");
    ctx.filter = "blur(6px) brightness(0.4)";
    try {
        ctx.drawImage(ctx.canvas, 0, 0);
    } catch {
        /* ignore */
    }
    ctx.filter = "none";
    ctx.fillStyle = "rgba(0,0,0,0.4)";
    ctx.fillRect(0, 0, canvasW, canvasH);
    ctx.restore();

    ctx.save();
    const msg = (step.message || "").trim();
    if (msg) {
        const side = step.messageSide || "top";
        const box = messageBoxRect(side, zx, zy, zw, zh, canvasW, canvasH);
        ctx.fillStyle = "rgba(10, 12, 22, 0.94)";
        ctx.strokeStyle = "rgba(200, 220, 255, 0.35)";
        ctx.lineWidth = 1.25;
        const r = 8;
        ctx.beginPath();
        ctx.moveTo(box.x + r, box.y);
        ctx.arcTo(box.x + box.w, box.y, box.x + box.w, box.y + box.h, r);
        ctx.arcTo(box.x + box.w, box.y + box.h, box.x, box.y + box.h, r);
        ctx.arcTo(box.x, box.y + box.h, box.x, box.y, r);
        ctx.arcTo(box.x, box.y, box.x + box.w, box.y, r);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        ctx.fillStyle = "rgba(235, 240, 255, 0.96)";
        ctx.font = `600 ${Math.max(12, Math.floor(canvasH * 0.022))}px system-ui, sans-serif`;
        ctx.textAlign = "left";
        ctx.textBaseline = "top";
        const lines = wrapText(ctx, msg, box.w - 20);
        let ty = box.y + 12;
        for (const line of lines.slice(0, 5)) {
            ctx.fillText(line, box.x + 10, ty);
            ty += Math.max(16, canvasH * 0.028);
        }
    }
    ctx.restore();
}

function wrapText(ctx: CanvasRenderingContext2D, text: string, maxW: number): string[] {
    const words = text.split(/\s+/).filter(Boolean);
    const lines: string[] = [];
    let cur = "";
    for (const w of words) {
        const test = cur ? `${cur} ${w}` : w;
        if (ctx.measureText(test).width > maxW && cur) {
            lines.push(cur);
            cur = w;
        } else {
            cur = test;
        }
    }
    if (cur) lines.push(cur);
    return lines;
}

/** @deprecated kept for any leftover imports */
export function advancePracticeGuideIfNeeded(
    steps: PracticeGuideStepDef[] | undefined,
    stepIndex: number,
    cursorNx: number,
    cursorNy: number,
): number {
    const next = tickPracticeGuide(steps, { triggerIndex: stepIndex, frozen: false, revealIndex: -1 }, cursorNx, cursorNy);
    return next.triggerIndex;
}
